import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ColumnLayout,
  OverviewMetricKey,
  OverviewTotalsCurrency,
  TableKey,
  UserPreferencesResponse,
} from "@unbox/api";
import type { HeroKey } from "@unbox/db";
import { useState } from "react";
import { toast } from "sonner";
import { handleError } from "@/api/error";
import { client } from "./client";

const userPreferencesQueryKey = ["user-preferences"];

export const useUserPreferences = () => {
  return useQuery({
    queryKey: userPreferencesQueryKey,
    queryFn: async () => {
      const res = await client["user-preferences"].$get();
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

export const useDismissHero = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (heroKey: HeroKey) => {
      const res = await client["user-preferences"].heroes[":heroKey"].dismiss.$post({ param: { heroKey } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onMutate: async (heroKey: HeroKey) => {
      await queryClient.cancelQueries({ queryKey: userPreferencesQueryKey });
      const previous = queryClient.getQueryData<UserPreferencesResponse>(userPreferencesQueryKey);
      queryClient.setQueryData<UserPreferencesResponse>(userPreferencesQueryKey, (current) => ({
        tableColumns: {},
        ...current,
        dismissedHeroes: { ...current?.dismissedHeroes, [heroKey]: new Date().toISOString() },
      }));
      return { previous };
    },
    onError: (_error, _heroKey, context) => {
      queryClient.setQueryData(userPreferencesQueryKey, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesQueryKey });
    },
  });
};

export const useSaveTableColumns = (tableKey: TableKey) => {
  const queryClient = useQueryClient();

  return useMutation({
    // One queue per table, so an older save can never land after a newer one.
    scope: { id: `table-columns-${tableKey}` },
    mutationFn: async (layout: ColumnLayout) => {
      const res = await client["user-preferences"].tables[":tableKey"].columns.$put({
        param: { tableKey },
        json: layout,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onMutate: async (layout: ColumnLayout) => {
      await queryClient.cancelQueries({ queryKey: userPreferencesQueryKey });
      const previous = queryClient.getQueryData<UserPreferencesResponse>(userPreferencesQueryKey);
      queryClient.setQueryData<UserPreferencesResponse>(userPreferencesQueryKey, (current) => ({
        dismissedHeroes: {},
        ...current,
        tableColumns: { ...current?.tableColumns, [tableKey]: layout },
      }));
      return { previous };
    },
    onError: (_error, _layout, context) => {
      queryClient.setQueryData(userPreferencesQueryKey, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesQueryKey });
    },
  });
};

const pinnedMetricsQueryKey = [...userPreferencesQueryKey, "overview-pinned-metrics"];

const fetchPinnedMetrics = async () => {
  const res = await client["user-preferences"]["overview-pinned-metrics"].$get();
  if (!res.ok) throw await handleError(res);
  return res.json();
};

type PinnedMetrics = Awaited<ReturnType<typeof fetchPinnedMetrics>>;

export const useOverviewPinsPreference = () => {
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: pinnedMetricsQueryKey, queryFn: fetchPinnedMetrics });

  const mutation = useMutation({
    scope: { id: "overview-pinned-metrics" },
    mutationFn: async (metrics: OverviewMetricKey[]) => {
      const res = await client["user-preferences"]["overview-pinned-metrics"].$put({ json: { metrics } });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onMutate: async (metrics) => {
      await queryClient.cancelQueries({ queryKey: pinnedMetricsQueryKey });
      const previous = queryClient.getQueryData<PinnedMetrics>(pinnedMetricsQueryKey);
      queryClient.setQueryData<PinnedMetrics>(pinnedMetricsQueryKey, { metrics });
      return { previous };
    },
    onError: (_error, _metrics, context) => {
      queryClient.setQueryData(pinnedMetricsQueryKey, context?.previous);
      toast.error("Couldn't save your metrics. Try again in a moment.");
    },
  });

  const [pending, setPending] = useState<OverviewMetricKey[] | null>(null);

  const save = (metrics: OverviewMetricKey[]) => {
    setPending(metrics);
    mutation.mutate(metrics, {
      onSettled: () => setPending((current) => (current === metrics ? null : current)),
    });
  };

  return { saved: pending ?? query.data?.metrics ?? null, isLoading: query.isLoading, save };
};

const totalsCurrencyQueryKey = [...userPreferencesQueryKey, "overview-totals-currency"];

const fetchTotalsCurrency = async () => {
  const res = await client["user-preferences"]["overview-totals-currency"].$get();
  if (!res.ok) throw await handleError(res);
  return res.json();
};

type TotalsCurrencyPreference = Awaited<ReturnType<typeof fetchTotalsCurrency>>;

export const useOverviewTotalsCurrencyPreference = () => {
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: totalsCurrencyQueryKey, queryFn: fetchTotalsCurrency });

  const mutation = useMutation({
    scope: { id: "overview-totals-currency" },
    mutationFn: async (currency: OverviewTotalsCurrency) => {
      const res = await client["user-preferences"]["overview-totals-currency"].$put({ json: { currency } });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onMutate: async (currency) => {
      await queryClient.cancelQueries({ queryKey: totalsCurrencyQueryKey });
      const previous = queryClient.getQueryData<TotalsCurrencyPreference>(totalsCurrencyQueryKey);
      queryClient.setQueryData<TotalsCurrencyPreference>(totalsCurrencyQueryKey, { currency });
      return { previous };
    },
    onError: (_error, _currency, context) => {
      queryClient.setQueryData(totalsCurrencyQueryKey, context?.previous);
      toast.error("Couldn't save your currency. Try again in a moment.");
    },
  });

  return { saved: query.data?.currency ?? null, isLoading: query.isLoading, save: mutation.mutate };
};
